import { Injectable } from '@angular/core';
import {
  CardBrand,
  PaymentAccount,
  AccountPaymentMethod,
  BankAccountType,
  CreatePaymentAccountRequestV1,
  OriginSystem,
  PaymentMethodFormDetails,
  CreatePaymentAccountResponseV1,
  CreatePaymentAccountPayload,
  DbCardNetwork,
  Country,
  PaymentAccountsPushNotificationMetadata,
} from '../models';
import { HttpErrorResponse } from '@angular/common/http';
import { CreditCardType } from '@arc/aureus';
import {
  AchInformation,
  AddPaymentMethodForm,
  CreditCardInformation,
} from '@arc/unified-payments-frontend/shared-payment-accounts';

@Injectable({
  providedIn: 'root',
})
export class PaymentAccountsHelperService {
  constructor() {}

  buildPaymentAccountRequest(
    payload: CreatePaymentAccountPayload,
    paymentMethod: PaymentMethodFormDetails,
  ): CreatePaymentAccountRequestV1 {
    return this.populatePaymentAccountRequestV1(
      payload.originatingApplication,
      payload.originSystem,
      payload.buyerAccountIdentifiers,
      payload.externalUserUrn,
      paymentMethod,
      payload.emailAddress,
    );
  }

  /* istanbul ignore next */
  mapWpxPaymentAccountCreated(
    addPaymentForm: AddPaymentMethodForm,
    savePaymentAccount: boolean,
    paymentMethod: PaymentMethodFormDetails,
    paymentAccountData: PaymentAccountsPushNotificationMetadata,
  ) {
    const { creditCard, billingInformation } = addPaymentForm.getRawValue();
    const { address } = billingInformation;
    const paymentAccount: PaymentAccount = {
      accountLast4: paymentAccountData.accountLast4,
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      paymentAccountIdentifierBurn: paymentAccountData.paymentAccountId,
      paymentMethod: AccountPaymentMethod.Card,
      postalCode: address.postalCode,
      province: address.state,
      cardNetwork: paymentAccountData.cardNetwork as DbCardNetwork,
      isOneTimePaymentAccount: !savePaymentAccount, // The service no longer returns this, so we have to set it based on the user's choice
      paymentMethodFormDetails: paymentMethod ? paymentMethod : null,
      displayName: paymentAccountData.displayName,
    };
    paymentAccount.nameOnCard = creditCard.name;
    paymentAccount.expirationMonth = paymentAccountData.expirationMonth;
    paymentAccount.expirationYear = paymentAccountData.expirationYear;
    paymentAccount.cardType = this.getAureusCardTypeFromCardNetwork(
      paymentAccountData.cardNetwork as DbCardNetwork,
    );
    return paymentAccount;
  }

  mapPaymentAccountCreated(
    paymentAccountResponse: CreatePaymentAccountResponseV1,
    savePaymentAccount: boolean,
    paymentMethod?: PaymentMethodFormDetails,
  ) {
    const createdPaymentAccount = paymentAccountResponse.paymentAccount;

    const paymentAccount: PaymentAccount = {
      accountLast4: createdPaymentAccount.accountLast4,
      address1: createdPaymentAccount.address1,
      address2: createdPaymentAccount.address2,
      bankAccountType: createdPaymentAccount.bankAccountType,
      bankName: createdPaymentAccount.bankName,
      city: createdPaymentAccount.city,
      country: createdPaymentAccount.country,
      emailAddress: createdPaymentAccount.emailAddress,
      paymentAccountIdentifierBurn:
        createdPaymentAccount.paymentAccountIdentifierBurn,
      paymentMethod: createdPaymentAccount.paymentMethod,
      phoneNumber: createdPaymentAccount.phoneNumber,
      postalCode: createdPaymentAccount.postalCode,
      province: createdPaymentAccount.province,
      cardNetwork: createdPaymentAccount.cardNetwork,
      isOneTimePaymentAccount: !savePaymentAccount, // The service no longer returns this, so we have to set it based on the user's choice
      paymentMethodFormDetails: paymentMethod ? paymentMethod : null,
      displayName: createdPaymentAccount.displayName,
    };

    if (paymentAccount.paymentMethod === AccountPaymentMethod.Ach) {
      paymentAccount.nameOnBankAccount =
        createdPaymentAccount.nameOnBankAccount;
    } else {
      paymentAccount.nameOnCard = createdPaymentAccount.nameOnCard;
      paymentAccount.expirationMonth =
        paymentAccountResponse.paymentAccount.expirationMonth;
      paymentAccount.expirationYear =
        paymentAccountResponse.paymentAccount.expirationYear;
      paymentAccount.cardType = this.getAureusCardTypeFromCardNetwork(
        paymentAccountResponse.paymentAccount.cardNetwork,
      );
    }

    return paymentAccount;
  }

  getAchPaymentAccountDisplayNameToSave(accountNumber: string): string {
    const accountNumberDigits = accountNumber.slice(accountNumber.length - 4);

    return `BANK - ${accountNumberDigits}`;
  }

  getCardPaymentAccountDisplayNameToSave(
    cardType: string,
    cardNumber: string,
  ): string {
    const cardNumberDigits = cardNumber.slice(cardNumber.length - 4);

    return `${cardType.toUpperCase()} - ${cardNumberDigits}`;
  }

  getCardBrandFromCardNetwork(cardNetwork: DbCardNetwork): CardBrand {
    switch (cardNetwork.toLocaleUpperCase()) {
      case 'V':
        return 'VISA';
      case 'M':
        return 'MC';
      case 'D':
        return 'DISC';
      case 'A':
        return 'AMEX';
      default:
        return 'UNKNOWN';
    }
  }

  getAureusCardTypeFromCardNetwork(cardNetwork: DbCardNetwork): CreditCardType {
    switch (cardNetwork.toLocaleUpperCase()) {
      case 'V':
        return 'visa';
      case 'M':
        return 'mastercard';
      case 'D':
        return 'discover';
      case 'A':
        return 'american-express';
      default:
        return 'unknown';
    }
  }

  private populatePaymentAccountRequestV1(
    originatingApplication: string,
    originSystem: OriginSystem,
    buyerAccountIdentifiers: string[],
    externalUserUrn: string,
    paymentMethod: PaymentMethodFormDetails,
    emailAddress: string,
  ): CreatePaymentAccountRequestV1 {
    const { paymentMethodInformation, billingInformation, savePaymentAccount } =
      paymentMethod;
    const paymentRequest: CreatePaymentAccountRequestV1 = {
      address1: billingInformation.address.address1,
      address2: billingInformation.address.address2,
      btPayContext: {
        application: originatingApplication,
        paymentAccountsSources: [OriginSystem[originSystem]],
      },
      buyerAccounts: buyerAccountIdentifiers,
      city: billingInformation.address.city,
      countryIsoAlpha2: billingInformation.address.country,
      province: billingInformation.address.state,
      postalCode: billingInformation.address.postalCode,
      isOneTimePaymentAccount: !savePaymentAccount,
      externalUserUrn: externalUserUrn,
      emailAddress: emailAddress,
    };

    switch (paymentMethodInformation.kind) {
      case 'ach':
        this.populateAchPaymentMethodInfo(
          paymentRequest,
          paymentMethodInformation,
        );
        break;
      case 'creditCard':
        this.populateCardPaymentMethodInfo(
          paymentRequest,
          paymentMethodInformation,
        );
        break;
    }

    return paymentRequest;
  }

  private populateAchPaymentMethodInfo(
    paymentAccountRequest: CreatePaymentAccountRequestV1,
    achInfo: AchInformation,
  ) {
    const paymentRequest = paymentAccountRequest;
    paymentRequest.bankName = paymentRequest.displayName;
    paymentRequest.bankAccountNumber = achInfo.accountNumber;
    paymentRequest.bankAccountType = this.mapBankAccountType(
      achInfo.bankAccountType,
    );
    paymentRequest.nameOnAccount = achInfo.nameOnAccount;
    paymentRequest.routingNumber = this.getRoutingNumber(achInfo);
    paymentRequest.paymentMethod = AccountPaymentMethod.Ach;
    paymentRequest.displayName = this.getAchPaymentAccountDisplayNameToSave(
      achInfo.accountNumber,
    );
    paymentRequest.bankName = paymentRequest.displayName;
  }

  private populateCardPaymentMethodInfo(
    paymentAccountRequest: CreatePaymentAccountRequestV1,
    cardInfo: CreditCardInformation,
  ) {
    const paymentRequest = paymentAccountRequest;
    paymentRequest.cardNumber = cardInfo.number;
    paymentRequest.nameOnAccount = cardInfo.name;
    paymentRequest.expirationMonth = +cardInfo.expirationMonth;
    paymentRequest.expirationYear = +cardInfo.expirationYear;
    paymentRequest.paymentMethod = AccountPaymentMethod.Card;
    paymentRequest.displayName = this.getCardPaymentAccountDisplayNameToSave(
      cardInfo.type,
      cardInfo.number,
    );
  }

  parsePaymentAccountError(error: HttpErrorResponse): string {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    let errorMessage = (error.error?.error ?? error.error) as string;

    if (Array.isArray(error.error)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      errorMessage = (error.error[0]?.message ?? error.error[0]) as string;
    }

    let paymentAccountError = errorMessage;
    const errorIndex = errorMessage.lastIndexOf(
      'Error Creating Payment Account:',
    );

    if (errorIndex !== -1) {
      const regex = new RegExp('([."}]+){2}$');
      paymentAccountError = errorMessage.slice(errorIndex).replace(regex, '');
    }

    return paymentAccountError;
  }

  private mapBankAccountType(bankAccountType: string) {
    switch (bankAccountType) {
      case 'personal_checking':
        return BankAccountType.PersonalChecking;
      case 'personal_savings':
        return BankAccountType.PersonalSavings;
      case 'business_checking':
        return BankAccountType.BusinessChecking;
      case 'business_savings':
        return BankAccountType.BusinessSavings;
      default:
        return null;
    }
  }

  private getRoutingNumber(achInformation: AchInformation) {
    let { routingNumber } = achInformation;

    switch (achInformation.region) {
      case 'CA':
        routingNumber = `${achInformation.transitNumber}${achInformation.institutionNumber}`;
        break;
    }

    return routingNumber;
  }
}
